import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import Control from "../../common/Control";
import { Pill } from "../../common/DataList";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import JaugeRemplissageEp from "./JaugeRemplissageEp";
import {
  LEGENDE_EP,
  MOIS,
  anneesDisponibles,
  compterEps,
  epsDuJour,
  epsDuMois,
  etatEp,
  filtrerEps,
  heureEp,
  juresAAfficher,
  libelleAccessible,
  nomSecteur,
  pastillesEp,
  prochaineEp,
  remplissageEp,
} from "./calendrierEp";

// ============================================================
// Vue compacte du calendrier des EP (maquette 09) : une carte par mois, une
// ligne par EP (jour, heure, secteur alignés), pastilles pour ce qui sort de
// l'ordinaire seulement. L'année tient sur un écran. Données et actions :
// useCalendrierEp.
// ============================================================

export const CLE_MASQUER_ANNULEES = "espoar.boa.calendrierEp.masquerAnnulees";

export const LigneEp = ({ ep, sectors, maintenant, onOpen }) => {
  const etat = etatEp(ep);
  const dt = DateTime.fromISO(ep.beginAt).setLocale("fr");
  const jures = juresAAfficher(ep, maintenant);
  const remplissage = remplissageEp(ep);
  return (
    <button
      type="button"
      className={`ligne-ep ligne-ep--${etat.id}`}
      onClick={onOpen}
      aria-label={`${libelleAccessible(ep, sectors)}. Ouvrir le détail`}
      data-etat={etat.id}
    >
      <span className="ligne-ep__jour">
        <small>{dt.toFormat("ccc")}</small>
        {dt.toFormat("dd")}
      </span>
      <span className="ligne-ep__heure">{heureEp(ep)}</span>
      <span className="ligne-ep__secteur">{nomSecteur(sectors, ep.sector)}</span>
      <span className="ligne-ep__fin">
        {pastillesEp(ep).map((p) => (
          <Pill key={p.id} tone={p.ton} icon={p.icone}>
            {p.libelle}
          </Pill>
        ))}
        {jures > 0 && (
          <span className="ligne-ep__jures">
            <i className="fa fa-user-check" aria-hidden="true" />
            {jures} juré{jures > 1 ? "s" : ""}
          </span>
        )}
      </span>
      <span className="ligne-ep__jauge">
        {remplissage && <JaugeRemplissageEp remplissage={remplissage} complete={!!ep.isFull} />}
      </span>
    </button>
  );
};

const VueCompacteEp = ({ calendrier }) => {
  const { maintenant, eps, sectors, year, setYear, isLoading, openEp, creerEp } = calendrier;
  const [secteur, setSecteur] = useState("");
  const [masquerAnnulees, setMasquerAnnulees] = usePersistedState(CLE_MASQUER_ANNULEES, false);

  const annees = useMemo(() => anneesDisponibles(maintenant).map((a) => ({ id: a, name: String(a) })), [maintenant]);
  const optionsSecteurs = useMemo(() => [{ id: "", name: "Tous" }, ...sectors.map((s) => ({ id: s.id, name: s.name }))], [sectors]);
  const epsFiltrees = useMemo(() => filtrerEps(eps, { secteur, masquerAnnulees }), [eps, secteur, masquerAnnulees]);
  const duJour = useMemo(() => epsDuJour(epsFiltrees, maintenant), [epsFiltrees, maintenant]);
  const prochaine = useMemo(() => prochaineEp(epsFiltrees, maintenant), [epsFiltrees, maintenant]);
  const anneeCourante = Number(year) === maintenant.year;
  const indexAnnee = annees.findIndex((a) => a.id === Number(year));
  const chargementInitial = isLoading && !eps.length;

  return (
    <>
      <div className="list-card cal-barre">
        <div className="cal-barre__annee" role="group" aria-label="Année affichée">
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={() => setYear(annees[indexAnnee - 1].id)}
            disabled={indexAnnee <= 0}
            aria-label="Année précédente"
          >
            <i className="fa fa-chevron-left" aria-hidden="true" />
          </button>
          <ChoixChips name="annee" legend="Année" legendSrOnly size="sm" options={annees} value={Number(year)} onChange={(v) => setYear(Number(v))} />
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={() => setYear(annees[indexAnnee + 1].id)}
            disabled={indexAnnee < 0 || indexAnnee >= annees.length - 1}
            aria-label="Année suivante"
          >
            <i className="fa fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
        <ChoixChips name="secteur" legend="Secteur" variant="inline" size="sm" options={optionsSecteurs} value={secteur} onChange={setSecteur} />
        <Control
          margin="my-0"
          type="checkbox"
          name="masquerAnnulees"
          label="Masquer les EP annulées"
          checked={masquerAnnulees}
          change={(e) => setMasquerAnnulees(e.target.checked)}
        />
        <ul className="cal-legende" aria-label="Légende des états">
          {LEGENDE_EP.map((etat) => (
            <li key={etat.id}>
              <Pill tone={etat.ton} icon={etat.icone}>
                {etat.libelle}
              </Pill>
            </li>
          ))}
          <li>
            <Pill tone="warn" icon="fa-history">
              Secours
            </Pill>
          </li>
          <li>
            <Pill tone="warn" icon="fa-exclamation-triangle">
              Bientôt complète
            </Pill>
          </li>
          <li>
            <Pill tone="warn" icon="fa-lock">
              Complète
            </Pill>
          </li>
          <li className="cal-legende__note">sans pastille : planifiée</li>
        </ul>
      </div>

      {anneeCourante && (
        <section className="list-card cal-aujourdhui" aria-labelledby="cal-aujourdhui-titre">
          <h2 id="cal-aujourdhui-titre" className="cal-aujourdhui__titre">
            Aujourd'hui
          </h2>
          {chargementInitial ? (
            <p className="cal-aujourdhui__texte" role="status">
              Chargement…
            </p>
          ) : duJour.length ? (
            <div className="cal-aujourdhui__liste">
              {duJour.map((ep) => (
                <LigneEp key={ep.id} ep={ep} sectors={sectors} maintenant={maintenant} onOpen={() => openEp(maintenant.month - 1, ep)} />
              ))}
            </div>
          ) : (
            <p className="cal-aujourdhui__texte">
              <i className="fa fa-calendar-check" aria-hidden="true" />
              Aucune EP.
              {prochaine && (
                <>
                  {" "}
                  Prochaine le{" "}
                  <b>
                    {DateTime.fromISO(prochaine.beginAt).setLocale("fr").toFormat("cccc d MMMM")} à {heureEp(prochaine)}
                  </b>
                  , secteur {nomSecteur(sectors, prochaine.sector)}.
                </>
              )}
            </p>
          )}
        </section>
      )}

      <div className="cal-mois-grille" aria-busy={isLoading || undefined}>
        {MOIS.map((mois, index) => {
          const epsMois = epsDuMois(epsFiltrees, index);
          const courant = anneeCourante && index === maintenant.month - 1;
          const passe = Number(year) < maintenant.year || (anneeCourante && index < maintenant.month - 1);
          return (
            <section
              key={mois}
              className={`list-card cal-mois${courant ? " cal-mois--courant" : ""}${passe ? " cal-mois--passe" : ""}`}
              aria-labelledby={`cal-mois-${index}`}
            >
              <header className="cal-mois__entete">
                <h2 id={`cal-mois-${index}`} className="cal-mois__titre">
                  {mois}
                </h2>
                {courant && <span className="cal-mois__courant">ce mois-ci</span>}
                <span className="cal-mois__compte">{chargementInitial ? "…" : compterEps(epsMois.length)}</span>
                <span data-priv="edit_ep">
                  <button
                    type="button"
                    className="cal-mois__ajouter"
                    onClick={() => creerEp(index)}
                    aria-label={`Ajouter une EP en ${mois.toLowerCase()}`}
                    title={`Ajouter une EP en ${mois.toLowerCase()}`}
                  >
                    <i className="fa fa-plus" aria-hidden="true" />
                  </button>
                </span>
              </header>
              {chargementInitial ? (
                <div className="cal-mois__squelette" aria-hidden="true">
                  <span className="skeleton-bar" style={{ width: "70%" }} />
                </div>
              ) : epsMois.length ? (
                <div className="cal-mois__liste">
                  {epsMois.map((ep) => (
                    <LigneEp key={ep.id} ep={ep} sectors={sectors} maintenant={maintenant} onOpen={() => openEp(index, ep)} />
                  ))}
                </div>
              ) : (
                <p className="cal-mois__vide">Aucune EP planifiée.</p>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
};

export default VueCompacteEp;
